// QuickLogIssue (R22) — fast walkthrough issue capture. Pick the property once,
// then fire off issues: type/dictate a quick note (optional — a photo can BE the
// issue), attach one or many photos, or dump a whole camera roll as one issue per
// photo. Each add lands on the Issues board INSTANTLY (text create), while photos
// upload in the background (throttled) so nothing blocks. Edit type/note later.
const API = '/api/assetops';
import { shrinkForUpload, enqueuePhotos, pendingCountMatching } from '../../lib/photoUploader';
import { EVIDENCE_ACCEPT } from '../lib/mediaKind';   // R116 #5: after evidence takes video too
import { isBoardUnknown, offerBoardRetry } from '../lib/boardRetry';   // review R120 #14 (round 6)

export interface QuickLogContext {
  sourceContext: 'cleaning_task' | 'inspection' | 'standalone';
  listingId?: string;
  listingName?: string;
  turnId?: string;
  inspectionId?: string;
  createdBy?: string;
  onLogged?: (count: number) => void;
  // R26: preload existing issues for this inspection/turn so the list is
  // persistent — reopening shows everything already logged, editable.
  preload?: boolean;
  // R26: live count callback (fires on preload/add/remove) — the inspection
  // step bar uses it to keep its "Step 1 · Issues (N)" label honest.
  onCount?: (n: number) => void;
  // R30: preset the composer category (e.g. 'damage' for the cleaner's
  // Report damage button).
  defaultCategory?: string;
  // R30: listings to rank FIRST in the property picker (the cleaner's
  // assigned-today homes) — "scrolling is annoying".
  priorityListings?: { id: string; name: string }[];
}

const CATEGORIES: { v: string; label: string }[] = [
  { v: 'maintenance', label: '🔧 Maintenance' },
  { v: 'cleaning', label: '🧹 Cleaning' },
  { v: 'ffe', label: '🛋 FF&E' },
  { v: 'damage', label: '💥 Damage' },
  { v: 'supply', label: '📦 Supply' },
  { v: 'safety', label: '⚠️ Safety' },
  { v: 'access', label: '🔑 Access' },
  { v: 'other', label: '• Other' },
];
const SEVERITIES: { v: string; label: string }[] = [
  { v: 'low', label: 'Low' }, { v: 'med', label: 'Medium' }, { v: 'high', label: 'High' }, { v: 'emergency', label: 'Emergency' },
];
const SpeechRec: any = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition || null;

// R26: room/area tag — "broken lamp" should always say WHICH room.
const ROOMS = ['Kitchen', 'Living Room', 'Dining', 'Primary Bedroom', 'Bedroom 2', 'Bedroom 3', 'Bedroom 4', 'Bathroom 1', 'Bathroom 2', 'Bathroom 3', 'Hallway', 'Laundry', 'Garage', 'Patio / Outdoor', 'Entry', 'Other'];
const FOLLOW_UPS: { v: string; label: string }[] = [
  { v: '', label: '⏰ follow-up?' },
  { v: 'before_next_checkin', label: 'before next check-in' },
  { v: 'during_stay', label: 'during guest stay' },
  { v: 'after_checkout', label: 'after checkout' },
];

interface Row { id: string; title: string; note: string; category: string; severity: string; room: string; fixed: boolean; follow_up: string; thumb?: string; isVideo?: boolean; uploading: number; failed?: number; uploadedOk?: boolean;
  // R109: after-photo proof for "fixed it myself", and the supply the note implies
  afterN?: number; afterUp?: number; fixedSkip?: string; supply?: { item: string; order_id?: string; dismissed?: boolean } | null; }
// R109 #a: why there's no after photo (same list as the office prompt)
const AFTER_SKIP = ['Fixed before I could take a photo', "Can't show it in a photo", 'Phone / camera problem', 'Nothing to show'];

export class QuickLogIssue {
  private ctx: QuickLogContext;
  private root!: HTMLDivElement;
  private listingId = '';
  private listingName = '';
  private listings: { id: string; name: string }[] | null = null;
  private logged: Row[] = [];
  private cText = '';
  // R44 (Mark): a real DETAILS field — "scuffed baseboards" is the issue,
  // "left of the master door, pretty deep, fix before the 15th" is the note.
  // The note field always existed in the data; this is its first input.
  private cNote = '';
  private cNoteOpen = false;
  private cCat = 'maintenance';
  private cSev = 'med';
  // Sticky on purpose — inspectors walk room by room, so the room they picked
  // stays selected for the next issue until they change it.
  private cRoom = '';
  private cFiles: File[] = [];
  private embedded = false;
  private destroyed = false;
  private rec: any = null;
  private recording = false;
  private recBase = '';
  private recFinal = '';
  private msg = '';
  // background upload pool
  private queue: (() => Promise<void>)[] = [];
  private active = 0;

  constructor(ctx: QuickLogContext) {
    this.ctx = ctx;
    this.listingId = ctx.listingId || '';
    this.listingName = ctx.listingName || '';
    if (ctx.sourceContext === 'cleaning_task') this.cCat = 'cleaning';
    if (ctx.defaultCategory && CATEGORIES.some(c => c.v === ctx.defaultCategory)) this.cCat = ctx.defaultCategory;
  }
  static open(ctx: QuickLogContext): void { new QuickLogIssue(ctx).mount(); }
  // R26: render the logger INLINE inside a host element (no modal chrome) —
  // the inspection's Step 1 embeds it as THE issue capture surface.
  static embed(host: HTMLElement, ctx: QuickLogContext): QuickLogIssue {
    const inst = new QuickLogIssue(ctx);
    inst.embedded = true;
    void inst.mountInto(host);
    return inst;
  }

  async mountInto(host: HTMLElement): Promise<void> {
    this.injectStyle();
    this.root = document.createElement('div') as HTMLDivElement;
    this.root.className = 'qli-inline';
    host.innerHTML = '';
    host.appendChild(this.root);
    if (this.ctx.preload) await this.preloadExisting();
    if (this.destroyed) return;
    this.draw();
  }

  // R26: the page redraws its DOM freely — the embed instance survives by
  // MOVING its root (state, listeners, composer text, upload badges intact)
  // into the freshly rendered host instead of re-instantiating.
  reattach(host: HTMLElement): void {
    if (this.destroyed || !this.root) return;
    host.innerHTML = '';
    host.appendChild(this.root);
  }

  // Tear down a replaced/exited embed: stop any live dictation, silence its
  // callbacks. Without this an orphaned instance keeps the mic hot and its
  // late onCount writes corrupt the next inspection's count.
  destroy(): void {
    this.destroyed = true;
    try { if (this.rec) this.rec.stop(); } catch (_) { /* noop */ }
    this.rec = null;
    this.recording = false;
    try { this.root?.remove(); } catch (_) { /* noop */ }
  }

  // Authoritative in-memory list for the page (step-2 recap) — includes rows
  // whose creates already resolved, without racing a server refetch.
  getLogged(): any[] {
    return this.logged.map(r => ({
      id: r.id, title: r.title, note: r.note, category: r.category, severity: r.severity,
      room: r.room, fixed: r.fixed, follow_up: r.follow_up || null,
      photos: r.thumb && !r.isVideo ? [r.thumb] : [], status: 'open',
    }));
  }

  // Load everything already logged for this inspection/turn so the list is
  // persistent — "once it saves it disappears" was the core complaint.
  private async preloadExisting(): Promise<void> {
    const q = this.ctx.inspectionId ? `inspection_id=${encodeURIComponent(this.ctx.inspectionId)}`
      : this.ctx.turnId ? `turn_id=${encodeURIComponent(this.ctx.turnId)}` : '';
    if (!q) return;
    try {
      const j = await fetch(`${API}/issues/log?${q}`).then(r => r.ok ? r.json() : null);
      const rows = (j && j.issues) || [];
      this.logged = rows.map((x: any) => ({
        id: x.id, title: x.title || '', note: x.note || '', category: x.category || 'other',
        severity: x.severity || 'med', room: x.room || '', fixed: !!x.fixed, follow_up: x.follow_up || '',
        thumb: (Array.isArray(x.photos) && x.photos[0]) || (Array.isArray(x.videos) && x.videos[0]) || undefined,
        isVideo: !(Array.isArray(x.photos) && x.photos[0]) && !!(Array.isArray(x.videos) && x.videos[0]),
        uploading: 0,
        afterN: Array.isArray(x.after_photos) ? x.after_photos.length : 0,
        fixedSkip: (x.fixed_skip && x.fixed_skip.reason) || undefined,
        supply: x.supply_hint && x.supply_hint.item ? x.supply_hint : null,
      }));
      this.notifyCount();
    } catch (_) { /* list starts empty — adds still work */ }
  }

  private notifyCount(): void {
    if (this.destroyed) return;
    if (this.ctx.onCount) { try { this.ctx.onCount(this.logged.length); } catch (_) { /* noop */ } }
  }

  private esc(s: any): string {
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
  }

  async mount(): Promise<void> {
    this.injectStyle();
    this.root = document.createElement('div');
    this.root.className = 'qli-wrap';
    document.body.appendChild(this.root);
    // R30: ANY context without a preset listing gets the searchable picker
    // (the cleaner's Report damage opens with no property chosen).
    if (!this.listingId) await this.loadListings();
    this.draw();
  }

  private close(): void {
    try { if (this.rec) this.rec.stop(); } catch (_) { /* noop */ }
    const n = this.logged.length;
    const pending = this.active + this.queue.length;
    this.root.remove();
    if (n) this.toast(`${n} issue${n === 1 ? '' : 's'} logged${pending ? ' (photos finishing upload…)' : ''} — route them on the Issues board.`);
    if (this.ctx.onLogged) { try { this.ctx.onLogged(n); } catch (_) { /* noop */ } }
  }

  private async loadListings(): Promise<void> {
    // include=onboarding: onboarding-stage homes (no reservations yet, e.g.
    // "OB - 4825 Point Loma Blvd") are exactly where walkthrough issues get logged.
    const l = await fetch('/api/listings/active?include=onboarding').then(r => r.ok ? r.json() : null).catch(() => null);
    const arr = Array.isArray(l) ? l : (l && l.listings) || [];
    let all = arr.map((x: any) => ({ id: String(x.id), name: String(x.short_name || x.name || x.id) }))
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
    // R30: assigned-today homes rank first (deduped by id).
    const prio = (this.ctx.priorityListings || []).map(p => ({ id: String(p.id), name: String(p.name || p.id) }));
    if (prio.length) {
      const prioIds = new Set(prio.map(p => p.id));
      all = [...prio, ...all.filter(x => !prioIds.has(x.id))];
    }
    this.listings = all;
  }

  // ── background upload pool (throttled so 50 photos don't hammer the server) ──
  private enqueue(job: () => Promise<void>): void { this.queue.push(job); this.pump(); }
  private pump(): void {
    while (this.active < 3 && this.queue.length) {
      const job = this.queue.shift()!;
      this.active++;
      job().catch(() => { /* best-effort */ }).finally(() => { this.active--; this.pump(); });
    }
  }
  private uploadTo(id: string, files: File[], kind?: 'after'): void {
    if (kind === 'after') { this.uploadAfter(id, files); return; }
    const row = this.logged.find(r => r.id === id);
    if (row) { row.uploading += files.length; row.failed = 0; }   // new attempt clears old failure
    // R27 (Mark: "idk if the photo took"): show the photo on the row THE MOMENT
    // it's picked — the local file is the preview; the upload rides behind it.
    if (row && !row.thumb && files[0]) this.setRowThumb(id, files[0]);
    this.updateBadge(id);
    for (const f of files) {
      this.enqueue(async () => {
        let ok = false;
        try {
          // R38: compress on-device first — the raw camera-roll dump was
          // 4-8MB/photo over cellular; ~300KB JPEG uploads in seconds and
          // skips the server's slow HEIC transcode.
          const { blob, name } = await shrinkForUpload(f);
          const d = new FormData(); d.append('photo', blob, name); d.append('by', this.ctx.createdBy || this.actorName());   // R116 #5: who added it
          const res = await fetch(`${API}/issues/log/${id}/photo`, { method: 'POST', body: d });
          ok = res.ok;
        } catch (_) { ok = false; }
        const r = this.logged.find(x => x.id === id);
        if (r) {
          r.uploading = Math.max(0, r.uploading - 1);
          if (ok) r.uploadedOk = true; else r.failed = (r.failed || 0) + 1;
        }
        this.updateBadge(id);
      });
    }
  }

  // R109 #a: AFTER photos ride the same compress + upload pool, stored apart
  // from the "before" photos so the board can show them side by side.
  // R116 #5 (Jorge: video evidence, many at once): photos AND clips go into the
  // app's PERSISTED background queue (lib/photoUploader — survives closing this
  // sheet or the app, clips 1-wide, the server's size cap) tagged with who took
  // them and the time of the fix (for_resolved_at); the strip counts from the
  // queue and from its 'surge-photo-uploaded' / '-dropped' events.
  private afterListening = false;
  private uploadAfter(id: string, files: File[]): void {
    const url = `${API}/issues/log/${id}/photo`;
    this.listenAfter();
    const row = this.logged.find(r => r.id === id);
    if (row) row.afterUp = (row.afterUp || 0) + files.length;   // instant feedback while the files are prepared
    this.updateAfterStrip(id);
    void enqueuePhotos(url, files, { kind: 'after', by: this.ctx.createdBy || this.actorName(), for_resolved_at: new Date().toISOString() }, 'after photo')
      .catch(() => 0)
      .then((n) => {
        const r = this.logged.find(x => x.id === id);
        if (r) r.afterUp = pendingCountMatching(url, (f) => f.kind === 'after');
        if (!n) this.toast('Those after files couldn\'t be queued — too large? Try a shorter clip.');
        this.updateAfterStrip(id);
      });
  }
  private listenAfter(): void {
    if (this.afterListening) return;
    this.afterListening = true;
    const idOf = (url: string) => { const m = /\/issues\/log\/([^/]+)\/photo$/.exec(String(url || '')); return m ? decodeURIComponent(m[1]) : ''; };
    const gone = () => this.destroyed || !this.root || !this.root.isConnected;
    const onUp = (e: Event) => {
      if (gone()) { off(); return; }
      const d = (e as CustomEvent).detail || {};
      if ((d.fields || {}).kind !== 'after') return;
      const id = idOf(d.url); const r = this.logged.find(x => x.id === id);
      if (!r) return;
      r.afterUp = pendingCountMatching(String(d.url), (f) => f.kind === 'after');
      r.afterN = (r.afterN || 0) + 1; r.fixedSkip = undefined;
      this.updateAfterStrip(id);
    };
    const onDrop = (e: Event) => {
      if (gone()) { off(); return; }
      const d = (e as CustomEvent).detail || {};
      if ((d.fields || {}).kind !== 'after') return;
      const id = idOf(d.url); const r = this.logged.find(x => x.id === id);
      if (!r) return;
      r.afterUp = pendingCountMatching(String(d.url), (f) => f.kind === 'after');
      this.toast(`An after file didn't upload — ${d.note || 'try it again'}.`);
      this.updateAfterStrip(id);
    };
    const off = () => { window.removeEventListener('surge-photo-uploaded', onUp); window.removeEventListener('surge-photo-dropped', onDrop); this.afterListening = false; };
    window.addEventListener('surge-photo-uploaded', onUp);
    window.addEventListener('surge-photo-dropped', onDrop);
  }
  private afterStripHtml(r: Row): string {
    if (!r.fixed) return '';
    const inner = (r.afterUp || 0) > 0 ? `<span class="qli-after">📷 uploading ${(r.afterUp || 0) > 1 ? `${r.afterUp} after files` : 'after file'}…</span>`
      : (r.afterN || 0) > 0 ? `<span class="qli-after ok">📷 After ✓${(r.afterN || 0) > 1 ? ` (${r.afterN})` : ''}</span><label class="qli-after-add" title="More after photos or a video">＋<input type="file" accept="${EVIDENCE_ACCEPT}" multiple data-afterphoto="${this.esc(r.id)}"></label>`
      : r.fixedSkip ? `<span class="qli-after">No after photo — ${this.esc(r.fixedSkip)}</span><label class="qli-after-add" title="Add after photos or a video">📷<input type="file" accept="${EVIDENCE_ACCEPT}" multiple data-afterphoto="${this.esc(r.id)}"></label>`
      : `<label class="qli-after need" title="Photos or a short video — several at once">📷 Add AFTER photos / video<input type="file" accept="${EVIDENCE_ACCEPT}" multiple data-afterphoto="${this.esc(r.id)}"></label>
         <select class="qli-sel" data-afterskip="${this.esc(r.id)}"><option value="">No photo — why?</option>${AFTER_SKIP.map(x => `<option>${this.esc(x)}</option>`).join('')}</select>`;
    return inner;
  }
  // Swap the after strip in place (other rows may be mid-edit) and re-bind
  // only its own controls.
  private updateAfterStrip(id: string): void {
    const el = this.root?.querySelector<HTMLElement>(`[data-afterstrip="${CSS.escape(id)}"]`);
    const r = this.logged.find(x => x.id === id);
    if (!el || !r) return;
    el.innerHTML = this.afterStripHtml(r);
    this.bindAfterStrip(el);
  }
  private bindAfterStrip(scope: ParentNode): void {
    scope.querySelectorAll<HTMLInputElement>('[data-afterphoto]').forEach(el => el.addEventListener('change', () => {
      const id = el.dataset.afterphoto!; const files = Array.from(el.files || []); el.value = '';
      if (files.length) this.uploadAfter(id, files);
    }));
    scope.querySelectorAll<HTMLSelectElement>('[data-afterskip]').forEach(el => el.addEventListener('change', () => {
      const id = el.dataset.afterskip!; const r = this.logged.find(x => x.id === id);
      if (!r || !el.value) return;
      r.fixedSkip = el.value;
      void this.patch(id, { fixed_skip_reason: el.value });
      this.updateAfterStrip(id);
    }));
  }
  // R109 #e (Jesse: "Dirty mirror… no windex — ping worker about potential
  // supply request need?"): when the note names a supply that's out, ask
  // right there instead of leaving it buried in the note.
  private supplyStripHtml(r: Row): string {
    const s = r.supply;
    if (!s || !s.item || s.dismissed) return '';
    if (s.order_id) return `<div class="qli-supply done">✓ 📦 ${this.esc(s.item)} requested</div>`;
    return `<div class="qli-supply">📦 Out of <b>${this.esc(s.item)}</b>? <button data-supreq="${this.esc(r.id)}">Request it</button><button class="ghost" data-supno="${this.esc(r.id)}">No thanks</button></div>`;
  }
  private updateSupplyStrip(id: string): void {
    const el = this.root?.querySelector<HTMLElement>(`[data-supplystrip="${CSS.escape(id)}"]`);
    const r = this.logged.find(x => x.id === id);
    if (!el || !r) return;
    el.innerHTML = this.supplyStripHtml(r);
    this.bindSupplyStrip(el);
  }
  private bindSupplyStrip(scope: ParentNode): void {
    scope.querySelectorAll<HTMLButtonElement>('[data-supno]').forEach(el => el.addEventListener('click', () => {
      const id = el.dataset.supno!; const r = this.logged.find(x => x.id === id);
      if (!r || !r.supply) return;
      r.supply = { ...r.supply, dismissed: true };
      void this.patch(id, { supply_hint_dismissed: true });
      this.updateSupplyStrip(id);
    }));
    scope.querySelectorAll<HTMLButtonElement>('[data-supreq]').forEach(el => el.addEventListener('click', () => void this.requestSupply(el.dataset.supreq!, false)));
  }
  // R109 #e: one supply request for a logged row. Review R120 #14 (round 6):
  // the Issues board unreadable (503 board_unknown, nothing ordered) → plain
  // words + Try again, which re-sends the same request (the row's CURRENT
  // button is found again: the strip may have been redrawn meanwhile).
  private async requestSupply(id: string, force: boolean): Promise<void> {
    const r = this.logged.find(x => x.id === id);
    if (!r || !r.supply) return;
    const btn = () => this.root?.querySelector<HTMLButtonElement>(`[data-supreq="${CSS.escape(id)}"]`) || null;
    const busy = (on: boolean) => { const b = btn(); if (b) { b.disabled = on; b.textContent = on ? 'Requesting…' : 'Request it'; } };
    busy(true);
    const send = (f: boolean) => fetch(`${API}/issues/log/${encodeURIComponent(id)}/supply-need`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ by: this.ctx.createdBy || this.actorName(), ...(f ? { force_order: true } : {}) }) });
    try {
      let res = await send(force); let j = await res.json().catch(() => ({} as any));
      if (!force && res.status === 409 && j.code === 'cleaning_miss') {
        if (!confirm(j.error)) { busy(false); return; }
        force = true;
        res = await send(true); j = await res.json().catch(() => ({} as any));
      }
      if (isBoardUnknown(res.status, j)) { busy(false); const f = force; offerBoardRetry(() => this.requestSupply(id, f)); return; }
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      r.supply = { ...r.supply, item: j.item || r.supply.item, order_id: j.order_id };
      this.toast(`📦 ${r.supply.item} requested — the office will see it.`);
      this.updateSupplyStrip(id);
    } catch (e: any) { busy(false); this.toast(`Couldn't request it — ${e.message}`); }
  }

  // Swap a row's category-emoji placeholder for the just-picked photo, in
  // place (no redraw — the inspector may be typing in another row).
  private setRowThumb(id: string, file: File): void {
    const r = this.logged.find(x => x.id === id);
    if (!r || r.thumb) return;
    r.thumb = URL.createObjectURL(file);
    r.isVideo = /^video\//.test(file.type);
    const rowEl = this.root?.querySelector(`.qli-lrow[data-row="${CSS.escape(id)}"]`);
    const dot = rowEl?.querySelector('.qli-ldot');
    if (dot) {
      const d = document.createElement('div');
      d.className = 'qli-lthumb';
      d.innerHTML = r.isVideo
        ? `<video src="${this.esc(r.thumb)}" muted></video><span class="qli-vtag">▶</span>`
        : `<img src="${this.esc(r.thumb)}">`;
      dot.replaceWith(d);
    }
  }

  // Upload state is now three-valued and HONEST: uploading → ✓ saved | ⚠ failed.
  // The old badge vanished identically on success and failure.
  private badgeFor(row: Row | undefined): { text: string; cls: string } {
    if (!row) return { text: '', cls: 'qli-up' };
    if (row.uploading > 0) return { text: `⤴ ${row.uploading}`, cls: 'qli-up' };
    if (row.failed) return { text: `⚠ ${row.failed} failed — tap ＋📷 to retry`, cls: 'qli-up bad' };
    if (row.uploadedOk) return { text: '✓ photo saved', cls: 'qli-up ok' };
    return { text: '', cls: 'qli-up' };
  }
  private updateBadge(id: string): void {
    const el = this.root?.querySelector<HTMLElement>(`[data-up="${CSS.escape(id)}"]`);
    if (!el) return;
    const b = this.badgeFor(this.logged.find(r => r.id === id));
    el.textContent = b.text;
    el.className = b.cls;
  }

  private draw(): void {
    if (this.destroyed) return;
    const needPicker = !this.ctx.listingId;
    const lOpts = (this.listings || []).map(l => `<option value="${this.esc(l.name)}"></option>`).join('');
    const havePlace = !!this.listingId;

    const composer = !havePlace ? `<div class="qli-need">Choose a property above to start logging.</div>` : `
      <div class="qli-composer">
        <div class="qli-thumbs" id="qli-thumbs"></div>
        <div class="qli-crow">
          <label class="qli-cam" title="Attach photo/video to this issue">📷<input type="file" id="qli-files" accept="image/*,video/*" capture="environment" multiple></label>
          <div class="qli-note-wrap">
            <input id="qli-text" class="qli-in" maxlength="160" value="${this.esc(this.cText)}" placeholder="What's the issue? (optional if you add a photo)" autocomplete="off">
            ${SpeechRec ? `<button type="button" class="qli-mic${this.recording ? ' on' : ''}" id="qli-mic" title="Dictate">🎤</button>` : ''}
          </div>
        </div>
        <div class="qli-crow" style="margin-top:2px;">
          ${this.cNoteOpen
            ? `<textarea id="qli-details" maxlength="2000" placeholder="Details — how bad, exactly where, by when…" style="width:100%; min-height:54px; font:inherit; font-size:13px; padding:8px 10px; border:1px solid var(--border,#e2e8f0); border-radius:10px; resize:vertical;">${this.esc(this.cNote)}</textarea>`
            : `<button type="button" id="qli-details-toggle" style="background:none; border:none; color:var(--accent,#2563eb); font-size:12.5px; cursor:pointer; padding:0;">＋ details (how bad, exactly where, by when)</button>`}
        </div>
        <div class="qli-crow2">
          <select id="qli-room" class="qli-sel" title="Room / area — stays selected between issues"><option value="">📍 Room?</option>${ROOMS.map(r => `<option value="${r}"${r === this.cRoom ? ' selected' : ''}>${r}</option>`).join('')}</select>
          <select id="qli-cat" class="qli-sel">${CATEGORIES.map(x => `<option value="${x.v}"${x.v === this.cCat ? ' selected' : ''}>${x.label}</option>`).join('')}</select>
          <select id="qli-sev" class="qli-sel">${SEVERITIES.map(x => `<option value="${x.v}"${x.v === this.cSev ? ' selected' : ''}>${x.label}</option>`).join('')}</select>
          <button class="qli-add" id="qli-add">Add ⏎</button>
        </div>
        <!-- R52 (Mark: "logging 1 issue with multiple photos logged each photo
             as a separate issue") — the dump is now an explicit CHOICE: the
             classic one-issue-per-photo fan-out, or ALL photos onto ONE issue. -->
        <div class="qli-bulk-h">📸 Dump camera roll</div>
        <div class="qli-bulk-row">
          <label class="qli-bulk">One issue <b>per photo</b><span class="qli-bulk-sub">notes &amp; rooms later</span><input type="file" id="qli-bulk" accept="image/*,video/*" multiple></label>
          <label class="qli-bulk">All photos → <b>one issue</b><span class="qli-bulk-sub">uses the note/type above</span><input type="file" id="qli-bulk-one" accept="image/*,video/*" multiple></label>
        </div>
      </div>`;

    const list = this.logged.length ? `
      <div class="qli-logged">
        <div class="qli-logged-h">${this.embedded ? `Logged for this inspection · ${this.logged.length}` : `Logged this walkthrough · ${this.logged.length}`}</div>
        ${this.logged.map(r => this.rowHtml(r)).join('')}
      </div>` : (this.embedded ? '<div class="qli-empty-note">No issues logged yet — clean walk so far. Everything you add here stays on this list, editable, until the inspection is done.</div>' : '');

    const body = `
          ${needPicker
            ? `<label class="qli-lab">Property
                 <input id="qli-listing" class="qli-in" list="qli-listing-dl" autocomplete="off" value="${this.esc(this.listingName)}" placeholder="Type to search ${(this.listings || []).length} properties…">
                 <datalist id="qli-listing-dl">${lOpts}</datalist></label>`
            : (this.embedded ? '' : `<div class="qli-place">📍 ${this.esc(this.listingName || this.listingId)}</div>`)}
          ${composer}
          ${this.msg ? `<div class="qli-msg ${/fail|error|choose/i.test(this.msg) ? 'bad' : 'good'}">${this.esc(this.msg)}</div>` : ''}
          ${list}`;

    if (this.embedded) {
      // Inline (inspection Step 1): no backdrop/head/foot — the page owns the
      // chrome. Uploads-in-flight indicator rides on the list header via badges.
      this.root.innerHTML = `<div class="qli-body qli-body-inline">${body}</div>`;
      this.bind();
      this.renderThumbs();
      return;
    }

    this.root.innerHTML = `
      <div class="qli-backdrop" data-qli-close></div>
      <div class="qli-card" role="dialog" aria-label="Log issues">
        <div class="qli-head">
          <div>
            <div class="qli-title">Log issues</div>
            <div class="qli-sub">Snap, note, add — as many as you need. They land on the Issues board.</div>
          </div>
          <button class="qli-x" data-qli-close aria-label="Close">✕</button>
        </div>
        <div class="qli-body">${body}</div>
        <div class="qli-foot">
          <span class="qli-count">${this.logged.length ? `${this.logged.length} logged${this.active + this.queue.length ? ` · ${this.active + this.queue.length} photo${this.active + this.queue.length === 1 ? '' : 's'} uploading` : ''}` : ''}</span>
          <button class="qli-btn primary" data-qli-close>${this.logged.length ? 'Done' : 'Close'}</button>
        </div>
      </div>`;
    this.bind();
    this.renderThumbs();
    if (havePlace) { const t = this.root.querySelector<HTMLInputElement>('#qli-text'); if (t) t.focus(); }
  }

  // Each logged row keeps its PHOTO visible and its note/type/severity editable
  // INLINE — no expand/collapse, so annotating 50 photos never loses your place.
  private rowHtml(r: Row): string {
    const thumb = r.thumb
      ? `<div class="qli-lthumb">${r.isVideo ? `<video src="${this.esc(r.thumb)}" muted></video><span class="qli-vtag">▶</span>` : `<img src="${this.esc(r.thumb)}">`}</div>`
      : `<div class="qli-ldot">${this.catEmoji(r.category)}</div>`;
    // R28 (Mark: "resolved by inspector... i'll obviously fix it but still wana
    // ding them"): the fixed-it-myself toggle shows in EVERY context — the
    // issue stays documented (and strike-routable) even though it's resolved.
    // Follow-up timing stays inspection-only (it's inspection semantics).
    const fixedBtn = `
          <button class="qli-fixed${r.fixed ? ' on' : ''}" data-fixed="${this.esc(r.id)}" title="Resolved by you on the spot — the issue stays documented for team accountability. To count it against whoever caused it, route it to Strike review on the Issues board.">${r.fixed ? '✔ fixed by me' : '✔ Fixed it myself'}</button>`;
    const inspCtl = fixedBtn + (this.ctx.sourceContext === 'inspection' ? `
          <select class="qli-sel qli-fu" data-fu="${this.esc(r.id)}" ${r.fixed ? 'disabled' : ''}>${FOLLOW_UPS.map(f => `<option value="${f.v}"${f.v === (r.follow_up || '') ? ' selected' : ''}>${f.label}</option>`).join('')}</select>` : '');
    return `<div class="qli-lrow${r.fixed ? ' is-fixed' : ''}" data-row="${this.esc(r.id)}">
      ${thumb}
      <div class="qli-lbody">
        <input class="qli-lnote" data-note="${this.esc(r.id)}" value="${this.esc(r.title)}" placeholder="Add a note…" maxlength="160">
        <div class="qli-lctl">
          <select class="qli-sel qli-room" data-room="${this.esc(r.id)}"><option value="">📍 Room?</option>${ROOMS.map(x => `<option value="${x}"${x === r.room ? ' selected' : ''}>${x}</option>`).join('')}</select>
          <select class="qli-sel" data-cat="${this.esc(r.id)}">${CATEGORIES.map(x => `<option value="${x.v}"${x.v === r.category ? ' selected' : ''}>${x.label}</option>`).join('')}</select>
          <select class="qli-sel" data-sev="${this.esc(r.id)}">${SEVERITIES.map(x => `<option value="${x.v}"${x.v === r.severity ? ' selected' : ''}>${x.label}</option>`).join('')}</select>
          ${inspCtl}
          <span class="${this.badgeFor(r).cls}" data-up="${this.esc(r.id)}">${this.esc(this.badgeFor(r).text)}</span>
          <label class="qli-lcam" title="Add more photos">＋📷<input type="file" accept="image/*,video/*" multiple data-addphoto="${this.esc(r.id)}"></label>
          <button class="qli-lx" data-rm="${this.esc(r.id)}" title="Delete this issue">✕</button>
        </div>
        <div class="qli-afterstrip" data-afterstrip="${this.esc(r.id)}">${this.afterStripHtml(r)}</div>
        <div data-supplystrip="${this.esc(r.id)}">${this.supplyStripHtml(r)}</div>
      </div>
    </div>`;
  }

  private catLabel(v: string): string { return (CATEGORIES.find(c => c.v === v) || { label: v }).label.replace(/^\S+\s/, ''); }
  private catEmoji(v: string): string { const m = (CATEGORIES.find(c => c.v === v) || { label: '•' }).label.match(/^\S+/); return m ? m[0] : '•'; }

  private renderThumbs(): void {
    const el = this.root.querySelector<HTMLElement>('#qli-thumbs');
    if (!el) return;
    if (!this.cFiles.length) { el.innerHTML = ''; return; }
    el.innerHTML = this.cFiles.map((f, i) => {
      const isVid = /^video\//.test(f.type);
      const url = URL.createObjectURL(f);
      return `<div class="qli-thumb">${isVid ? `<video src="${url}" muted></video><span class="qli-vtag">▶</span>` : `<img src="${url}">`}<button class="qli-thumb-x" data-thumb-del="${i}">✕</button></div>`;
    }).join('');
    el.querySelectorAll<HTMLElement>('[data-thumb-del]').forEach(b => b.addEventListener('click', () => { this.cFiles.splice(Number(b.dataset.thumbDel), 1); this.renderThumbs(); }));
  }

  private syncComposer(): void {
    const v = (sel: string) => this.root.querySelector<HTMLInputElement | HTMLSelectElement>(sel)?.value;
    const t = v('#qli-text'); if (t != null) this.cText = t;
    const n = this.root.querySelector<HTMLTextAreaElement>('#qli-details')?.value; if (n != null) this.cNote = n;
    const c = v('#qli-cat'); if (c != null) this.cCat = c;
    const s = v('#qli-sev'); if (s != null) this.cSev = s;
    const r = v('#qli-room'); if (r != null) this.cRoom = r;
  }

  private bind(): void {
    this.root.querySelectorAll<HTMLElement>('[data-qli-close]').forEach(el => el.addEventListener('click', () => this.close()));
    this.root.querySelector<HTMLInputElement>('#qli-listing')?.addEventListener('change', (e) => {
      const typed = (e.target as HTMLInputElement).value.trim();
      const hit = (this.listings || []).find(l => l.name.toLowerCase() === typed.toLowerCase());
      // R30: non-matching text CLEARS the selection — showing one name while a
      // different property is silently selected mis-files every issue logged.
      if (hit) { this.listingId = hit.id; this.listingName = hit.name; this.msg = ''; this.draw(); }
      else { this.listingId = ''; this.listingName = ''; this.draw(); }
    });
    this.root.querySelector<HTMLInputElement>('#qli-files')?.addEventListener('change', (e) => {
      const inp = e.target as HTMLInputElement;
      for (const f of Array.from(inp.files || [])) if (this.cFiles.length < 10) this.cFiles.push(f);
      inp.value = '';
      this.renderThumbs();
    });
    this.root.querySelector<HTMLInputElement>('#qli-bulk')?.addEventListener('change', (e) => {
      const inp = e.target as HTMLInputElement;
      const files = Array.from(inp.files || []);
      inp.value = '';
      if (files.length) void this.addBulk(files);
    });
    this.root.querySelector<HTMLInputElement>('#qli-bulk-one')?.addEventListener('change', (e) => {
      const inp = e.target as HTMLInputElement;
      const files = Array.from(inp.files || []);
      inp.value = '';
      if (files.length) void this.addBulkOne(files);
    });
    this.root.querySelectorAll<HTMLElement>('#qli-text,#qli-cat,#qli-sev,#qli-room,#qli-details').forEach(el => el.addEventListener('input', () => this.syncComposer()));
    // R44: expand the details textarea (state-backed — survives redraws).
    this.root.querySelector('#qli-details-toggle')?.addEventListener('click', () => { this.syncComposer(); this.cNoteOpen = true; this.draw(); });
    this.root.querySelector<HTMLInputElement>('#qli-text')?.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') { e.preventDefault(); void this.addOne(); }
    });
    if (SpeechRec) this.root.querySelector<HTMLElement>('#qli-mic')?.addEventListener('click', () => this.toggleMic());
    this.root.querySelector<HTMLButtonElement>('#qli-add')?.addEventListener('click', () => void this.addOne());
    // logged rows — inline edit (photo stays visible; saves on change, no re-draw)
    this.root.querySelectorAll<HTMLInputElement>('[data-note]').forEach(el => {
      const id = el.dataset.note!;
      el.addEventListener('input', () => { const r = this.logged.find(x => x.id === id); if (r) r.title = el.value; });
      el.addEventListener('change', async () => {
        const j = await this.patch(id, { title: el.value.trim() });
        const r = this.logged.find(x => x.id === id);
        if (r && j && j.issue && !(r.supply && (r.supply.order_id || r.supply.dismissed))) { r.supply = j.issue.supply_hint && j.issue.supply_hint.item ? j.issue.supply_hint : null; this.updateSupplyStrip(id); }
      });
    });
    this.bindAfterStrip(this.root);
    this.bindSupplyStrip(this.root);
    this.root.querySelectorAll<HTMLSelectElement>('[data-cat]').forEach(el => el.addEventListener('change', () => { const r = this.logged.find(x => x.id === el.dataset.cat); if (r) r.category = el.value; this.patch(el.dataset.cat!, { category: el.value }); }));
    this.root.querySelectorAll<HTMLSelectElement>('[data-sev]').forEach(el => el.addEventListener('change', () => {
      const r = this.logged.find(x => x.id === el.dataset.sev);
      const was = r ? r.severity : undefined;
      if (r) r.severity = el.value;
      // review R120 #14 (fix-verify): a raise the server refuses (the office dismissed it on the board) is undone here and said
      void this.patchOrUndo(el.dataset.sev!, { severity: el.value }, () => { if (r) r.severity = was as any; });
    }));
    this.root.querySelectorAll<HTMLSelectElement>('[data-room]').forEach(el => el.addEventListener('change', () => { const r = this.logged.find(x => x.id === el.dataset.room); if (r) r.room = el.value; this.patch(el.dataset.room!, { room: el.value }); }));
    // fixed ⟷ follow-up: mutually exclusive; redraw so the row state reads right
    this.root.querySelectorAll<HTMLElement>('[data-fixed]').forEach(el => el.addEventListener('click', () => {
      const r = this.logged.find(x => x.id === el.dataset.fixed);
      if (!r) return;
      const before = { fixed: r.fixed, follow_up: r.follow_up, fixedSkip: r.fixedSkip, afterN: r.afterN };
      r.fixed = !r.fixed;
      if (r.fixed) r.follow_up = '';
      else { r.fixedSkip = undefined; r.afterN = 0; }   // reopening retires the after photos + "why no photo" on the server too
      // review R120 #14 (fix-verify): Fixed / Reopen the server refuses (the office dismissed it on the board) is undone here and said
      void this.patchOrUndo(r.id, { fixed: r.fixed, fixed_by: r.fixed ? (this.ctx.createdBy || this.actorName()) : null }, () => { Object.assign(r, before); });
      this.draw();   // the after-photo strip appears under the row
    }));
    this.root.querySelectorAll<HTMLSelectElement>('[data-fu]').forEach(el => el.addEventListener('change', () => {
      const r = this.logged.find(x => x.id === el.dataset.fu);
      if (!r) return;
      r.follow_up = el.value;
      if (r.follow_up) r.fixed = false;
      void this.patch(r.id, { follow_up: r.follow_up || null });
      this.draw();
    }));
    this.root.querySelectorAll<HTMLElement>('[data-rm]').forEach(el => el.addEventListener('click', () => void this.remove(el.dataset.rm!)));
    this.root.querySelectorAll<HTMLInputElement>('[data-addphoto]').forEach(el => el.addEventListener('change', (e) => {
      const inp = e.target as HTMLInputElement; const id = inp.dataset.addphoto!;
      const files = Array.from(inp.files || []); inp.value = '';
      if (files.length) { this.uploadTo(id, files); this.updateFootCount(); }
    }));
  }

  private updateFootCount(): void {
    const el = this.root?.querySelector<HTMLElement>('.qli-count');
    if (el) el.textContent = this.logged.length ? `${this.logged.length} logged${this.active + this.queue.length ? ` · ${this.active + this.queue.length} photos uploading` : ''}` : '';
  }

  // Add ONE issue (composer). Instant text create; photos upload in background.
  private async addOne(): Promise<void> {
    this.syncComposer();
    const title = this.cText.trim();
    if (!this.listingId) { this.msg = 'Choose a property first.'; this.draw(); return; }
    if (!title && !this.cFiles.length) { this.msg = 'Add a note or a photo.'; this.draw(); return; }
    const files = this.cFiles.slice();
    const category = this.cCat, severity = this.cSev, room = this.cRoom;
    const note = this.cNote.trim();   // R44: real details ride along
    const thumbFile = files[0];
    const thumb = thumbFile ? URL.createObjectURL(thumbFile) : undefined;
    const isVideo = !!(thumbFile && /^video\//.test(thumbFile.type));
    // clear composer immediately for the next issue (room stays — see cRoom)
    this.cText = ''; this.cNote = ''; this.cFiles = []; this.recBase = ''; this.recFinal = ''; this.msg = '';
    try {
      const j = await this.createIssue({ title, category, severity, room, note });
      if (!j) { this.msg = 'Failed to log — try again.'; this.draw(); return; }
      this.logged.unshift({ id: j.id, title: j.title, note, category, severity, room, fixed: false, follow_up: '', thumb, isVideo, uploading: 0, supply: j.supply_hint && j.supply_hint.item ? j.supply_hint : null });
      this.notifyCount();
      this.draw();
      if (files.length) this.uploadTo(j.id, files);
    } catch (_) { this.msg = 'Something went wrong.'; this.draw(); }
  }

  // Dump N photos → N photo-only issues at once, uploads in background.
  private async addBulk(files: File[]): Promise<void> {
    if (!this.listingId) { this.msg = 'Choose a property first.'; this.draw(); return; }
    const category = this.cCat, severity = this.cSev, room = this.cRoom;
    try {
      const res = await fetch(`${API}/issues/log/bulk`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listing_id: this.listingId, listing_name: this.listingName || undefined,
          source_context: this.ctx.sourceContext, created_by: this.ctx.createdBy || this.actorName(),
          turn_id: this.ctx.turnId, inspection_id: this.ctx.inspectionId,
          items: files.map(() => ({ category, severity, room })) }),
      });
      const j = await res.json().catch(() => ({} as any));
      const issues = (j && j.issues) || [];
      if (!res.ok || !issues.length) { this.msg = j.error || 'Bulk add failed.'; this.draw(); return; }
      // pair each new issue with its file (same order)
      issues.forEach((iss: any, i: number) => {
        const f = files[i];
        const isVideo = !!(f && /^video\//.test(f.type));
        this.logged.unshift({ id: iss.id, title: iss.title, note: '', category, severity, room, fixed: false, follow_up: '', thumb: f ? URL.createObjectURL(f) : undefined, isVideo, uploading: 0 });
      });
      this.msg = `${issues.length} photo issue${issues.length === 1 ? '' : 's'} added — set rooms & notes any time.`;
      this.notifyCount();
      this.draw();
      issues.forEach((iss: any, i: number) => { if (files[i]) this.uploadTo(iss.id, [files[i]]); });
    } catch (_) { this.msg = 'Bulk add failed.'; this.draw(); }
  }

  // R52: N photos → ONE issue. single_issue:true tells /issues/log/bulk to
  // create exactly one record (title from items[0]); every picked file then
  // rides the same per-photo upload pool onto that one id, so the existing
  // uploading/saved/failed badge is the progress line.
  private async addBulkOne(files: File[]): Promise<void> {
    if (!this.listingId) { this.msg = 'Choose a property first.'; this.draw(); return; }
    this.syncComposer();
    const title = this.cText.trim();
    const category = this.cCat, severity = this.cSev, room = this.cRoom;
    const note = this.cNote.trim();
    try {
      const res = await fetch(`${API}/issues/log/bulk`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listing_id: this.listingId, listing_name: this.listingName || undefined,
          source_context: this.ctx.sourceContext, created_by: this.ctx.createdBy || this.actorName(),
          turn_id: this.ctx.turnId, inspection_id: this.ctx.inspectionId,
          single_issue: true, count: 1,
          items: [{ title, category, severity, room, note: note || undefined }] }),
      });
      const j = await res.json().catch(() => ({} as any));
      const iss = ((j && j.issues) || [])[0];
      if (!res.ok || !iss) { this.msg = j.error || 'Add failed.'; this.draw(); return; }
      // composer consumed as this issue's title/details — clear for the next one
      this.cText = ''; this.cNote = ''; this.recBase = ''; this.recFinal = '';
      const f0 = files[0];
      this.logged.unshift({ id: iss.id, title: iss.title || title, note, category, severity, room,
        fixed: false, follow_up: '', thumb: f0 ? URL.createObjectURL(f0) : undefined,
        isVideo: !!(f0 && /^video\//.test(f0.type)), uploading: 0 });
      this.msg = `1 issue added — ${files.length} photo${files.length === 1 ? '' : 's'} attaching to it.`;
      this.notifyCount();
      this.draw();
      this.uploadTo(iss.id, files);
    } catch (_) { this.msg = 'Add failed.'; this.draw(); }
  }

  private async createIssue(fields: { title: string; category: string; severity: string; room?: string; note?: string }): Promise<any> {
    const res = await fetch(`${API}/issues/log`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        listing_id: this.listingId, listing_name: this.listingName || undefined,
        title: fields.title, note: fields.note || fields.title, category: fields.category, severity: fields.severity,
        room: fields.room || undefined,
        source_context: this.ctx.sourceContext, created_by: this.ctx.createdBy || this.actorName(),
        turn_id: this.ctx.turnId, inspection_id: this.ctx.inspectionId,
      }),
    });
    const j = await res.json().catch(() => ({} as any));
    return res.ok && j.issue ? j.issue : null;
  }

  // Persist an inline edit without re-rendering (keeps focus + scroll position).
  private async patch(id: string, fields: Record<string, unknown>): Promise<any> {
    const r = await fetch(`${API}/issues/log/${id}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields) }).catch(() => null);
    return r && r.ok ? r.json().catch(() => null) : null;
  }
  // Review R120 #14 (fix-verify): the same save, but an edit the server REFUSES (403: the office
  // dismissed this ticket on the Issues board; 503: it could not check the board) is put back on
  // screen and said in the server's plain words, so a refused change never looks saved.
  private async patchOrUndo(id: string, fields: Record<string, unknown>, undo: () => void): Promise<void> {
    const r = await fetch(`${API}/issues/log/${id}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields) }).catch(() => null);
    if (!r || r.ok || (r.status !== 403 && r.status !== 503)) return;
    const j = await r.json().catch(() => null);
    if (this.destroyed) return;
    undo();
    this.draw();
    this.toast(j && j.error ? String(j.error) : 'That change was not saved. Try again.');
  }

  // Review R120 #14 (round 6): the row leaves the list at once, but when the
  // server could not read the Issues board (503 board_unknown) nothing was
  // deleted: the row comes back where it was and Try again re-sends the DELETE.
  private async remove(id: string): Promise<void> {
    const at = this.logged.findIndex(r => r.id === id);
    const row = at >= 0 ? this.logged[at] : null;
    this.logged = this.logged.filter(r => r.id !== id);
    this.notifyCount();
    this.draw();
    const res = await fetch(`${API}/issues/log/${id}`, { method: 'DELETE' }).catch(() => null);
    if (!res || res.ok) return;
    const j = await res.json().catch(() => null);
    if (!isBoardUnknown(res.status, j) || !row || this.destroyed) return;
    if (!this.logged.some(r => r.id === id)) { this.logged.splice(Math.min(at, this.logged.length), 0, row); this.notifyCount(); this.draw(); }
    offerBoardRetry(() => this.remove(id));
  }

  private toggleMic(): void {
    const mic = this.root.querySelector<HTMLElement>('#qli-mic');
    const box = this.root.querySelector<HTMLInputElement>('#qli-text');
    if (this.recording) { try { this.rec && this.rec.stop(); } catch (_) { /* noop */ } return; }
    try {
      this.rec = new SpeechRec();
      this.rec.continuous = true; this.rec.interimResults = true; this.rec.lang = 'en-US';
      this.recBase = box ? box.value.trim() : ''; this.recFinal = '';
      const compose = (interim: string) => { const parts = [this.recBase, this.recFinal.trim(), interim.trim()].filter(Boolean); if (box) { box.value = parts.join(' '); this.cText = box.value; } };
      this.rec.onresult = (ev: any) => {
        let interim = '';
        for (let i = ev.resultIndex; i < ev.results.length; i++) { const seg = ev.results[i][0].transcript; if (ev.results[i].isFinal) this.recFinal += (this.recFinal ? ' ' : '') + seg.trim(); else interim += seg; }
        compose(interim);
      };
      this.rec.onend = () => { this.recording = false; if (mic) mic.classList.remove('on'); compose(''); };
      this.rec.onerror = () => { this.recording = false; if (mic) mic.classList.remove('on'); };
      this.rec.start();
      this.recording = true; if (mic) mic.classList.add('on');
    } catch (_) { this.recording = false; }
  }

  private actorName(): string {
    try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.full_name || p.email || sessionStorage.getItem('userRole') || '').trim(); }
    catch (_) { return String(sessionStorage.getItem('userRole') || ''); }
  }

  private toast(text: string): void {
    const t = document.createElement('div');
    t.className = 'qli-toast'; t.textContent = text;
    document.body.appendChild(t);
    setTimeout(() => t.classList.add('show'), 10);
    setTimeout(() => { t.classList.remove('show'); setTimeout(() => t.remove(), 300); }, 3600);
  }

  private injectStyle(): void {
    if (document.getElementById('qli-style')) return;
    const s = document.createElement('style');
    s.id = 'qli-style';
    s.textContent = `
      .qli-wrap { position:fixed; inset:0; z-index:9000; display:flex; align-items:flex-end; justify-content:center; }
      @media (min-width:640px){ .qli-wrap { align-items:center; } }
      .qli-backdrop { position:absolute; inset:0; background:rgba(15,23,42,.55); backdrop-filter:blur(2px); }
      .qli-card { position:relative; width:100%; max-width:560px; max-height:94vh; overflow:auto; background:var(--card,#fff); border-radius:18px 18px 0 0; box-shadow:0 20px 60px rgba(0,0,0,.3); display:flex; flex-direction:column; }
      @media (min-width:640px){ .qli-card { border-radius:18px; } }
      .qli-head { display:flex; justify-content:space-between; align-items:flex-start; padding:16px 18px 10px; }
      .qli-title { font-size:18px; font-weight:700; color:var(--text,#0f172a); }
      .qli-sub { font-size:12.5px; color:var(--text-muted,#64748b); margin-top:2px; }
      .qli-x { background:none; border:none; font-size:18px; color:var(--text-muted,#64748b); cursor:pointer; padding:2px 6px; }
      .qli-body { padding:2px 18px 14px; display:flex; flex-direction:column; gap:12px; }
      .qli-lab { display:flex; flex-direction:column; gap:5px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:var(--text-muted,#64748b); }
      .qli-in { padding:10px 12px; border:1px solid var(--border,#e2e8f0); border-radius:10px; font-size:15px; text-transform:none; letter-spacing:0; color:var(--text,#0f172a); background:var(--bg,#fff); width:100%; }
      .qli-place { font-size:14px; font-weight:600; color:var(--text,#0f172a); background:var(--bg-subtle,#f8fafc); border:1px solid var(--border,#e2e8f0); border-radius:10px; padding:9px 12px; }
      .qli-need { font-size:13px; color:var(--text-muted,#94a3b8); padding:8px 2px; }
      .qli-composer { border:1px solid var(--border,#e2e8f0); border-radius:14px; padding:12px; display:flex; flex-direction:column; gap:10px; background:var(--bg-subtle,#fbfcfe); }
      .qli-crow { display:flex; gap:8px; align-items:stretch; }
      .qli-cam { flex:none; width:46px; display:flex; align-items:center; justify-content:center; font-size:20px; border:1px solid var(--border,#e2e8f0); border-radius:10px; background:var(--card,#fff); cursor:pointer; }
      .qli-cam input, .qli-bulk input, .qli-lcam input { display:none; }
      .qli-note-wrap { position:relative; flex:1; }
      .qli-note-wrap .qli-in { padding-right:44px; }
      .qli-mic { position:absolute; right:6px; top:50%; transform:translateY(-50%); width:34px; height:34px; border-radius:50%; border:1px solid var(--border,#e2e8f0); background:var(--card,#fff); font-size:16px; cursor:pointer; }
      .qli-mic.on { background:#fee2e2; border-color:#fecaca; animation:qli-pulse 1.1s infinite; }
      @keyframes qli-pulse { 0%,100%{ box-shadow:0 0 0 0 rgba(239,68,68,.4);} 50%{ box-shadow:0 0 0 6px rgba(239,68,68,0);} }
      .qli-crow2 { display:flex; flex-wrap:wrap; gap:8px; }
      /* R80 (Jesse: the dropdown arrow sat over the words) — own the arrow:
         appearance:none + a right-anchored chevron + reserved padding, and
         squeezed labels ellipsize instead of running under the glyph. */
      .qli-sel { flex:1; min-width:0; padding:9px 26px 9px 10px; border:1px solid var(--border,#e2e8f0); border-radius:10px; font-size:13.5px; background:var(--card,#fff); color:var(--text,#0f172a); appearance:none; -webkit-appearance:none; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2364748b' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 9px center; text-overflow:ellipsis; white-space:nowrap; overflow:hidden; }
      .qli-add { flex:none; padding:9px 16px; border:none; border-radius:10px; background:var(--accent,#2563eb); color:#fff; font-size:14px; font-weight:600; cursor:pointer; white-space:nowrap; }
      .qli-bulk { display:block; text-align:center; font-size:12.5px; color:var(--accent,#2563eb); border:1px dashed var(--accent,#2563eb); border-radius:10px; padding:9px; cursor:pointer; background:var(--card,#fff); }
      /* R52: the dump is a two-way choice — per-photo fan-out vs one issue */
      .qli-bulk-h { text-align:center; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:var(--text-muted,#64748b); margin-bottom:-4px; }
      .qli-bulk-row { display:flex; gap:8px; }
      .qli-bulk-row .qli-bulk { flex:1 1 50%; min-width:0; }
      .qli-bulk-sub { display:block; font-size:10.5px; color:var(--text-muted,#94a3b8); margin-top:2px; }
      .qli-thumbs { display:flex; gap:8px; flex-wrap:wrap; }
      .qli-thumb, .qli-lthumb { position:relative; border-radius:8px; overflow:hidden; border:1px solid var(--border,#e2e8f0); }
      .qli-thumb { width:56px; height:56px; } .qli-lthumb { width:44px; height:44px; flex:none; }
      .qli-thumb img, .qli-thumb video, .qli-lthumb img, .qli-lthumb video { width:100%; height:100%; object-fit:cover; }
      .qli-vtag { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; color:#fff; font-size:15px; text-shadow:0 1px 3px rgba(0,0,0,.6); }
      .qli-thumb-x { position:absolute; top:2px; right:2px; width:18px; height:18px; border-radius:50%; border:none; background:rgba(15,23,42,.7); color:#fff; font-size:10px; cursor:pointer; line-height:1; }
      .qli-logged { display:flex; flex-direction:column; gap:6px; }
      .qli-logged-h { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:var(--text-muted,#64748b); margin-top:4px; }
      .qli-lrow { display:flex; align-items:flex-start; gap:10px; padding:8px 10px; border:1px solid var(--border-soft,#f1f5f9); border-radius:10px; background:var(--card,#fff); }
      .qli-lthumb { width:52px !important; height:52px !important; }
      .qli-ldot { width:52px; height:52px; flex:none; display:flex; align-items:center; justify-content:center; font-size:20px; background:var(--bg-subtle,#f8fafc); border-radius:8px; }
      .qli-lbody { flex:1; min-width:0; display:flex; flex-direction:column; gap:6px; }
      .qli-lnote { padding:7px 9px; border:1px solid var(--border,#e2e8f0); border-radius:8px; font-size:13.5px; width:100%; color:var(--text,#0f172a); background:var(--bg,#fff); }
      .qli-lctl { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
      .qli-lctl .qli-sel { flex:0 1 96px; font-size:12px; padding:5px 20px 5px 7px; min-width:0; background-position:right 6px center; }
      .qli-up { color:var(--accent,#2563eb); font-weight:600; font-size:11px; }
      .qli-up.ok { color:#16a34a; }
      .qli-up.bad { color:#b91c1c; }
      .qli-lcam { flex:none; font-size:11px; color:var(--text-muted,#64748b); border:1px solid var(--border,#e2e8f0); border-radius:7px; padding:5px 7px; cursor:pointer; background:var(--card,#fff); }
      .qli-lx { flex:none; margin-left:auto; width:26px; height:26px; border-radius:7px; border:1px solid var(--border,#e2e8f0); background:var(--card,#fff); color:var(--text-muted,#94a3b8); font-size:12px; cursor:pointer; }
      .qli-msg { font-size:13px; padding:9px 11px; border-radius:9px; }
      .qli-msg.bad { background:#fef2f2; color:#b91c1c; } .qli-msg.good { background:#f0fdf4; color:#15803d; }
      .qli-foot { position:sticky; bottom:0; display:flex; justify-content:space-between; align-items:center; gap:10px; padding:12px 18px; background:var(--card,#fff); border-top:1px solid var(--border-soft,#f1f5f9); }
      .qli-count { font-size:12.5px; color:var(--text-muted,#64748b); }
      .qli-btn { padding:11px 22px; border-radius:10px; font-size:14.5px; font-weight:600; cursor:pointer; border:1px solid transparent; }
      .qli-btn.primary { background:var(--accent,#2563eb); color:#fff; }
      .qli-toast { position:fixed; bottom:24px; left:50%; transform:translateX(-50%) translateY(12px); z-index:9500; background:#0f172a; color:#fff; padding:12px 18px; border-radius:12px; font-size:14px; box-shadow:0 8px 30px rgba(0,0,0,.3); opacity:0; transition:all .25s; max-width:90vw; text-align:center; }
      .qli-toast.show { opacity:1; transform:translateX(-50%) translateY(0); }
      /* R26: inline embed (inspection Step 1) — page owns the chrome */
      .qli-inline .qli-body-inline { display:flex; flex-direction:column; gap:12px; }
      .qli-empty-note { font-size:12.5px; color:var(--text-muted,#94a3b8); padding:4px 2px; }
      .qli-fixed { flex:none; border:1px solid var(--border,#e2e8f0); border-radius:7px; background:var(--card,#fff); color:var(--text-muted,#64748b); font-size:11px; font-weight:600; padding:5px 8px; cursor:pointer; min-height:30px; }
      .qli-fixed.on { background:#dcfce7; border-color:#86efac; color:#166534; }
      .qli-lrow.is-fixed { opacity:.75; } .qli-lrow.is-fixed .qli-lnote { text-decoration:line-through; }
      .qli-lrow.is-fixed .qli-afterstrip { opacity:1; }
      .qli-afterstrip:empty { display:none; }
      .qli-afterstrip { display:flex; gap:6px; align-items:center; flex-wrap:wrap; margin-top:6px; font-size:12px; }
      .qli-after { color:var(--text-muted,#64748b); }
      .qli-after.ok { color:#166534; font-weight:700; }
      .qli-after.need { display:inline-flex; align-items:center; gap:4px; padding:6px 10px; border:2px dashed #86efac; border-radius:8px; color:#166534; font-weight:700; cursor:pointer; background:#f0fdf4; min-height:34px; }
      .qli-after.need input, .qli-after-add input { display:none; }
      .qli-after-add { cursor:pointer; padding:3px 8px; border:1px solid var(--border,#e2e8f0); border-radius:7px; }
      .qli-supply { margin-top:6px; padding:6px 10px; border-radius:8px; background:#fffbeb; border:1px solid #fcd34d; font-size:12.5px; display:flex; gap:6px; align-items:center; flex-wrap:wrap; }
      .qli-supply button { border:1px solid #b45309; background:#b45309; color:#fff; border-radius:7px; padding:5px 10px; font-size:12px; font-weight:700; cursor:pointer; min-height:30px; }
      .qli-supply button.ghost { background:transparent; color:#92400e; border-color:#fcd34d; }
      .qli-supply.done { background:#f0fdf4; border-color:#86efac; color:#166534; font-weight:600; }
      .qli-fu { max-width:150px; }
      .qli-room { max-width:140px; }
      /* iOS zooms the page when a focused control is under 16px — walkthroughs
         are phone-first, so every input/select gets 16px on small screens.
         R33 #2: the composer's room/category/severity row was one cramped flex
         line at every width — on phones it wraps 2-up with a full-width Add. */
      @media (max-width:640px) {
        .qli-in, .qli-lnote, .qli-sel { font-size:16px; }
        .qli-lctl .qli-sel { min-width:0; flex:1 1 45%; font-size:16px; }   /* R80 review: must win the (0,2,0) base rule or iOS zooms on focus */
        .qli-fixed, .qli-lcam { min-height:38px; display:inline-flex; align-items:center; }
        .qli-composer { padding:10px; }
        .qli-crow2 { flex-wrap:wrap; }
        .qli-crow2 .qli-sel { flex:1 1 45%; min-width:0; max-width:none; }
        .qli-add { flex:1 1 100%; min-height:48px; font-size:15.5px; padding:11px 16px; }
        .qli-cam { width:52px; min-height:48px; }
        .qli-mic { width:38px; height:38px; }
        .qli-note-wrap .qli-in { padding-right:48px; }
        .qli-bulk { padding:12px 8px; }
        .qli-body { padding-left:12px; padding-right:12px; }
        .qli-lx { width:32px; height:32px; }
      }`;
    document.head.appendChild(s);
  }
}
